import type { Injector } from '@angular/core';
import { Dialog } from '@angular/cdk/dialog';
import { forkJoin } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {
  createBulkEditSectionsToken,
  ConfirmDialogComponent,
  FIELD_PERMISSION_CHECKER,
  ToastService,
  type BulkEditSection,
  type BulkPatchFragment,
  type BulkSelectionPayload,
  type FieldPermissionChecker,
} from '@zag/ui';
import { UsersManagementService } from './users-management.service';

function hasPermission(permission: string): (injector: Injector) => boolean {
  return injector => injector.get<FieldPermissionChecker>(FIELD_PERMISSION_CHECKER).hasPermission(permission);
}
import { PersonRolesBulkSectionComponent } from './person-roles-bulk-section/person-roles-bulk-section.component';
import { UserGroupsBulkSectionComponent } from './user-groups-bulk-section/user-groups-bulk-section.component';
import { UserLanguageBulkSectionComponent } from './user-language-bulk-section/user-language-bulk-section.component';
import { UserStatusBulkSectionComponent } from './user-status-bulk-section/user-status-bulk-section.component';

export const USERS_BULK_EDIT_SECTIONS_TOKEN = createBulkEditSectionsToken('users');

interface RolesState {
  roleIds: string[];
  mode: 'add' | 'remove';
}

interface GroupsState {
  groupIds: string[];
  mode: 'add' | 'remove';
}

interface StatusState {
  active: boolean;
}

interface LanguageState {
  language: 'en' | 'de' | 'fr' | 'it';
}

export const USERS_BULK_EDIT_SECTIONS: BulkEditSection[] = [
  {
    id: 'bulk-roles',
    icon: 'shield',
    titleKey: 'bulkEdit.users.roles.title',
    priority: 10,
    component: PersonRolesBulkSectionComponent,
    previewLabelKey: 'bulkEdit.users.roles.preview',
    appliesToArrayField: true,
    supportsClear: false,
    buildPatchFragment(state: unknown): BulkPatchFragment {
      const s = state as RolesState;
      if (!s?.roleIds?.length) return {};
      return {
        arrayOps: {
          roles: { op: s.mode === 'remove' ? 'remove' : 'append', items: s.roleIds.map(roleId => ({ roleId })) },
        },
      };
    },
    visibilityCheck: hasPermission('rbac:roles:read'),
    editCheck: hasPermission('rbac:users:update'),
  },
  {
    id: 'bulk-groups',
    icon: 'groups',
    titleKey: 'bulkEdit.users.groups.title',
    priority: 20,
    component: UserGroupsBulkSectionComponent,
    previewLabelKey: 'bulkEdit.users.groups.preview',
    appliesToArrayField: true,
    supportsClear: false,
    buildPatchFragment(state: unknown): BulkPatchFragment {
      const s = state as GroupsState;
      if (!s?.groupIds?.length) return {};
      return {
        arrayOps: {
          groups: { op: s.mode === 'remove' ? 'remove' : 'append', items: s.groupIds.map(groupId => ({ groupId })) },
        },
      };
    },
    visibilityCheck: hasPermission('rbac:groups:read'),
    editCheck: hasPermission('rbac:users:update'),
  },
  {
    id: 'bulk-status',
    icon: 'toggle_on',
    titleKey: 'bulkEdit.users.status.title',
    priority: 30,
    component: UserStatusBulkSectionComponent,
    previewLabelKey: 'bulkEdit.users.status.preview',
    appliesToArrayField: false,
    supportsClear: false,
    buildPatchFragment(state: unknown): BulkPatchFragment {
      const s = state as StatusState;
      return { patch: { active: { op: 'set', value: s?.active ?? true } } };
    },
    visibilityCheck: hasPermission('rbac:users:read'),
    editCheck: hasPermission('rbac:users:update'),
  },
  {
    id: 'bulk-language',
    icon: 'language',
    titleKey: 'bulkEdit.users.language.title',
    priority: 40,
    component: UserLanguageBulkSectionComponent,
    previewLabelKey: 'bulkEdit.users.language.preview',
    appliesToArrayField: false,
    supportsClear: false,
    buildPatchFragment(state: unknown): BulkPatchFragment {
      const s = state as LanguageState;
      return { patch: { preferredLanguage: { op: 'set', value: s?.language ?? 'en' } } };
    },
    visibilityCheck: hasPermission('rbac:users:read'),
    editCheck: hasPermission('rbac:users:update'),
  },
  {
    id: 'bulk-send-password-reset',
    icon: 'lock_reset',
    titleKey: 'bulkEdit.users.sendPasswordReset.title',
    priority: 50,
    previewLabelKey: 'bulkEdit.users.sendPasswordReset.preview',
    appliesToArrayField: false,
    supportsClear: false,
    buildPatchFragment: (): BulkPatchFragment => ({}),
    visibilityCheck: hasPermission('rbac:users:update'),
    editCheck: hasPermission('rbac:users:update'),
    directActionHandler(selectionPayload: BulkSelectionPayload, injector: Injector): void {
      const dialog = injector.get(Dialog);
      const translate = injector.get(TranslateService);
      const toast = injector.get(ToastService);
      const usersService = injector.get(UsersManagementService);

      if (!('ids' in selectionPayload)) {
        toast.error(translate.instant('bulkEdit.users.sendPasswordReset.filterNotSupported'));
        return;
      }

      const dialogRef = dialog.open(ConfirmDialogComponent, {
        panelClass: 'ai-chat-dialog-panel',
        backdropClass: 'ai-chat-dialog-backdrop',
        data: {
          title: 'bulkEdit.users.sendPasswordReset.confirmTitle',
          message: 'bulkEdit.users.sendPasswordReset.confirmMessage',
          confirmText: 'bulkEdit.users.sendPasswordReset.confirmButton',
        },
      });

      dialogRef.closed.subscribe(confirmed => {
        if (!confirmed) return;
        forkJoin(selectionPayload.ids.map(id => usersService.sendPasswordReset(id))).subscribe({
          next: () => toast.success(translate.instant('bulkEdit.users.sendPasswordReset.success')),
          error: () => toast.error(translate.instant('bulkEdit.users.sendPasswordReset.error')),
        });
      });
    },
  },
  {
    id: 'bulk-delete',
    icon: 'delete',
    titleKey: 'bulkEdit.users.delete.title',
    priority: 60,
    previewLabelKey: 'bulkEdit.users.delete.preview',
    appliesToArrayField: false,
    supportsClear: false,
    danger: true,
    buildPatchFragment: (): BulkPatchFragment => ({}),
    visibilityCheck: hasPermission('rbac:users:update'),
    editCheck: hasPermission('rbac:users:update'),
    directActionHandler(selectionPayload: BulkSelectionPayload, injector: Injector, onComplete?: () => void): void {
      const dialog = injector.get(Dialog);
      const translate = injector.get(TranslateService);
      const toast = injector.get(ToastService);
      const usersService = injector.get(UsersManagementService);

      if (!('ids' in selectionPayload)) {
        toast.error(translate.instant('bulkEdit.users.delete.filterNotSupported'));
        return;
      }

      const dialogRef = dialog.open(ConfirmDialogComponent, {
        panelClass: 'ai-chat-dialog-panel',
        backdropClass: 'ai-chat-dialog-backdrop',
        data: {
          title: 'bulkEdit.users.delete.confirmTitle',
          message: 'bulkEdit.users.delete.confirmMessage',
          confirmText: 'common.delete',
          destructive: true,
        },
      });

      dialogRef.closed.subscribe(confirmed => {
        if (!confirmed) return;
        forkJoin(selectionPayload.ids.map(id => usersService.deleteUser(id))).subscribe({
          next: () => {
            toast.success(translate.instant('bulkEdit.users.delete.success'));
            onComplete?.();
          },
          error: () => toast.error(translate.instant('bulkEdit.users.delete.error')),
        });
      });
    },
  },
];
